import { useInputControl } from "@conform-to/react";
import { LabelProps } from "@radix-ui/react-label";

import { Button, Field, Label, Slider } from "~/components";
import { FieldError, Form, Input, RadioGroup, RadioGroupItem, useForm, z } from "~/components/form";
import { AboveGroundIcon, BelowGroundIcon, MinusIcon, PlusIcon } from "~/icons";
import { cn } from "~/lib/utils";

export const filterSchema = z.object({
    fromQuestionnaire: z.enum(["", "true"]).optional(),
    mausoleumId: z.string().optional(),
    crypt: z.string().optional(),
    intermentRights: z.coerce.number().optional(),
    minimumPrice: z.coerce.number().optional(),
    maximumPrice: z.coerce.number().optional(),
    groundLevel: z.enum(["", "above-ground", "below-ground"]).optional(),
    burialType: z.enum(["", "cremation", "burial"]).optional(),
});

export type SearchParamFilters = z.infer<typeof filterSchema>;

export const RESET_FILTERS = "reset-filters";
export const APPLY_FILTERS = "apply-filters";
export const ADD_CART_ITEM = "add-cart-item";

export function ProductFiltersForm({
    filters,
    priceRange,
    intermentRightsRange,
}: {
    filters: SearchParamFilters;
    priceRange: { min: number; max: number };
    intermentRightsRange: { min: number; max: number };
}) {
    const [form, fields] = useForm<SearchParamFilters>(filterSchema, {
        defaultValue: {
            fromQuestionnaire: filters?.fromQuestionnaire,
            intermentRights: filters?.intermentRights || "2",
            minimumPrice: filters?.minimumPrice || priceRange.min,
            maximumPrice: filters?.maximumPrice || priceRange.max,
            groundLevel: filters?.groundLevel || undefined,
            burialType: filters?.burialType || undefined,
        },
    });

    const minPriceControl = useInputControl(fields.minimumPrice);
    const maxPriceControl = useInputControl(fields.maximumPrice);
    const intermentRightsControl = useInputControl(fields.intermentRights);

    function handlePriceOnChange([min, max]: [string, string]) {
        minPriceControl.change(min);
        maxPriceControl.change(max);
    }

    function updateintermentRights(name?: string) {
        const value = Number(intermentRightsControl.value);

        if (name === "increment") {
            if (value < intermentRightsRange.max) {
                intermentRightsControl.change((value + 1).toString());
            }
        } else {
            if (value > intermentRightsRange.min) {
                intermentRightsControl.change((value - 1).toString());
            }
        }
    }

    return (
        <>
            {/* FIX: Why does this work - something to do with modal children ?!?! */}
            <BelowGroundIcon className="w-10 h-10 hidden" />
            <Form method="post" form={form} className="flex flex-col gap-4 mt-4">
                <input type="hidden" name="fromQuestionnaire" value={filters?.fromQuestionnaire} />
                <Field>
                    <div className="flex flex-col gap-4">
                        <section className="flex items-center gap-8 justify-between">
                            <Label className="text-base font-medium grow" htmlFor={fields.intermentRights.id}>
                                Number of Internment Rights
                            </Label>
                            <section className="flex justify-end border border-primary rounded">
                                <button
                                    type="button"
                                    className={cn(
                                        "flex justify-center items-center w-10 h-10 outline-none",
                                        intermentRightsControl.value > intermentRightsRange.min
                                            ? "text-black"
                                            : "text-zinc-400 cursor-not-allowed",
                                    )}
                                    onClick={() => updateintermentRights()}
                                >
                                    <MinusIcon className="w-5 h-5 ml-2" />
                                </button>
                                <Input
                                    className={cn(
                                        "border-transparent rounded-none !outline-none flex justify-center items-center w-10 text-center",
                                        fields.intermentRights.errors && "border-red-600",
                                    )}
                                    name="intermentRights"
                                    type="number"
                                    max={intermentRightsRange.max}
                                    min={1}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    onClick={() => updateintermentRights("increment")}
                                    className={cn(
                                        "flex justify-center items-center w-10 h-10 outline-none",
                                        intermentRightsControl.value < intermentRightsRange.max
                                            ? "text-black"
                                            : "text-zinc-400 cursor-not-allowed",
                                    )}
                                >
                                    <PlusIcon className="w-5 h-5 mr-2" />
                                </button>
                            </section>
                        </section>
                        <p className="muted text-sm">
                            All spaces include the right for two interments, even if you are purchasing for only one
                            person.
                        </p>
                    </div>
                    <div className="h-5">
                        <FieldError name="intermentRights" />
                    </div>
                </Field>
                {priceRange.min === priceRange.max ? null : (
                    <>
                        <Label className="text-base font-medium">Price Range</Label>
                        <Slider
                            min={0}
                            max={priceRange.max}
                            step={500}
                            value={[Number(minPriceControl.value), Number(maxPriceControl.value)]}
                            onValueChange={([min, max]) => {
                                handlePriceOnChange([min.toString(), max.toString()]);
                            }}
                        />
                        <section className="flex justify-between gap-8">
                            <Field>
                                <Label className="text-sm font-medium" htmlFor={fields.minimumPrice.id}>
                                    Minimum Price
                                </Label>
                                <Input
                                    className={fields.minimumPrice.errors && "border-red-600"}
                                    name="minimumPrice"
                                    type="number"
                                    placeholder="$0"
                                    onChange={(e) => handlePriceOnChange([e.target.value, maxPriceControl.value])}
                                />
                                <div className="h-5">
                                    <FieldError name="minimumPrice" />
                                </div>
                            </Field>
                            <Field>
                                <Label className="text-sm font-medium" htmlFor={fields.maximumPrice.id}>
                                    Maximum Price
                                </Label>
                                <Input
                                    className={fields.maximumPrice.errors && "border-red-600"}
                                    name="maximumPrice"
                                    type="number"
                                    placeholder="$20,000+"
                                    onChange={(e) => handlePriceOnChange([minPriceControl.value, e.target.value])}
                                />
                                <div className="h-5">
                                    <FieldError name="maximumPrice" />
                                </div>
                            </Field>
                        </section>
                    </>
                )}
                <Field>
                    <section className="flex justify-between">
                        <Label className="text-base font-medium" htmlFor={fields.groundLevel.id}>
                            Ground Level
                        </Label>
                        <button
                            type="button"
                            onClick={() => form.update({ name: fields.groundLevel.name, value: "" })}
                            className="text-sm font-light leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 hover:underline"
                        >
                            clear
                        </button>
                    </section>
                    <RadioGroup name="groundLevel">
                        <RadioGroupLabel className="flex-col">
                            <div className="flex justify-between gap-4">
                                <AboveGroundIcon className="w-10 h-10" />
                                <RadioGroupItem value="above-ground" />
                            </div>
                            Above Ground
                        </RadioGroupLabel>
                        <RadioGroupLabel className="flex-col">
                            <div className="flex justify-between gap-4">
                                <BelowGroundIcon className="w-10 h-10" />
                                <RadioGroupItem value="below-ground" />
                            </div>
                            Below Ground
                        </RadioGroupLabel>
                    </RadioGroup>
                    <div className="h-5">
                        <FieldError name="groundLevel" />
                    </div>
                </Field>
                <Field>
                    <section className="flex justify-between">
                        <Label className="text-base font-medium" htmlFor={fields.burialType.id}>
                            Type of Burial
                        </Label>
                        <button
                            type="button"
                            onClick={() => form.update({ name: fields.burialType.name, value: "" })}
                            className="text-sm font-light leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 hover:underline"
                        >
                            clear
                        </button>
                    </section>
                    <RadioGroup name="burialType">
                        <RadioGroupLabel>
                            Cremation
                            <RadioGroupItem value="cremation" />
                        </RadioGroupLabel>
                        <RadioGroupLabel>
                            Burial
                            <RadioGroupItem value="burial" />
                        </RadioGroupLabel>
                    </RadioGroup>
                    <div className="h-5">
                        <FieldError name="burialType" />
                    </div>
                </Field>
                <section className="flex justify-between">
                    <Button className="uppercase" variant="outline" name="intent" value={RESET_FILTERS}>
                        Clear all
                    </Button>
                    <Button className="uppercase" type="submit" name="intent" value={APPLY_FILTERS}>
                        Apply Filters
                    </Button>
                </section>
            </Form>
        </>
    );
}

function RadioGroupLabel({ className, ...props }: LabelProps) {
    return (
        <Label
            className={cn(
                "flex gap-4 cursor-pointer justify-between",
                "border-2 border-black border-opacity-20 transition-opacity hover:border-opacity-50 w-full rounded-md p-4",
                "has-state-checked",
                className,
            )}
            {...props}
        />
    );
}
